import React, { useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import CreditEligiblePill from 'components/common/CreditEligiblePill';
import type { Agent } from 'api/types';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface AgentViewModalProps {
  isOpen: boolean;
  agent: Agent | null;
  onClose: () => void;
  onEdit?: (agent: Agent) => void;
  // Minimize-to-bar support
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (agentId: string) => void;
}

// ============================================================================
// READ-ONLY FIELD
// ============================================================================

const ReadOnlyField: React.FC<{ label: string; value?: string | number | boolean | null; span?: boolean }> = ({ label, value, span }) => (
  <div style={span ? { gridColumn: '1 / -1' } : undefined}>
    <label className="form-label">{label}</label>
    <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--form-border-color)' }}>
      {value != null && value !== '' ? (typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)) : '-'}
    </div>
  </div>
);

// ============================================================================
// CONSTANTS
// ============================================================================

const AGENT_TYPE_LABELS: Record<string, string> = {
  'Travel Agent': 'Travel Agent',
  'Tour Operator': 'Tour Operator',
  Wholesale: 'Wholesale',
};

// 'Net Rate' removed — option dropped from create/edit; legacy records still
// containing it will fall back to the raw string via `LABELS[code] || code`.
const COMMISSION_TYPE_LABELS: Record<string, string> = {
  Percentage: 'Percentage',
  'Flat Amount': 'Flat Amount',
};

// ============================================================================
// COMPONENT
// ============================================================================

const AgentViewModal: React.FC<AgentViewModalProps> = ({
  isOpen,
  agent,
  onClose,
  onEdit,
  tabId,
  moduleName,
  onRestoreView,
}) => {
  const { minimize } = useModalMinimize();

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !agent) return;
    minimize({
      id: `${tabId}-view-modal-${agent.id}`,
      title: `View - ${agent.agentCode}`,
      moduleName,
      tabId,
      onRestore: () => onRestoreView?.(agent.id),
    });
    onClose();
  }, [tabId, moduleName, agent, minimize, onClose, onRestoreView]);

  if (!isOpen || !agent) return null;

  const formatNumber = (val?: number) => val != null ? String(val) : '-';
  const formatPercent = (val?: number) => val != null ? `${val}%` : '-';
  const formatCurrency = (val?: number) => val != null ? val.toFixed(2) : '-';

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        <div className="modal-header">
          <h2 className="modal-title">Agent Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* --- Basic Information --- */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }}>
            <ReadOnlyField label="Agent Code" value={agent.agentCode} />
            <ReadOnlyField label="Agent Name" value={agent.agentName} />
            <ReadOnlyField label="Agent Type" value={agent.agentType ? (AGENT_TYPE_LABELS[agent.agentType] || agent.agentType) : undefined} />
          </div>

          {/* --- Status --- */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
            <div>
              <label className="form-label">Status</label>
              <div>
                <span className={`badge badge-${agent.isActive ? 'active' : 'inactive'}`}>
                  {agent.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          {/* --- Contact Information --- */}
          <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Contact Information</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }}>
              <ReadOnlyField label="Contact Person" value={agent.contactPerson} />
              <ReadOnlyField label="Phone" value={agent.phone} />
              <ReadOnlyField label="Email" value={agent.email} />
            </div>
          </div>

          {/* --- Address --- */}
          <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Address</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
              <ReadOnlyField label="Address" value={agent.address} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
              <ReadOnlyField label="City" value={agent.city} />
              <ReadOnlyField label="State" value={agent.state} />
              <ReadOnlyField label="Country" value={agent.country} />
              <ReadOnlyField label="PIN Code" value={agent.pincode} />
            </div>
          </div>

          {/* --- Commission & Credit --- */}
          <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>Commission & Credit</h3>
              <CreditEligiblePill eligible={agent.isCreditEligible} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1.5rem' }}>
              <ReadOnlyField label="Commission Type" value={agent.commissionType ? (COMMISSION_TYPE_LABELS[agent.commissionType] || agent.commissionType) : undefined} />
              <ReadOnlyField label="Commission %" value={formatPercent(agent.commissionPercent)} />
              <ReadOnlyField label="Credit Days" value={formatNumber(agent.creditDays)} />
              <ReadOnlyField label="Credit Limit" value={formatCurrency(agent.creditLimit)} />
            </div>
          </div>

          {/* --- Tax Information --- */}
          <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Tax Information</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              <ReadOnlyField label="GST Number" value={agent.gstNumber} />
              <ReadOnlyField label="PAN Number" value={agent.panNumber} />
            </div>
          </div>

          {/* --- Blocking Configuration --- */}
          <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem' }}>Blocking Configuration</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }}>
              <ReadOnlyField label="Allow Blocking" value={agent.allowBlocking} />
              <ReadOnlyField label="Max Blocks" value={formatNumber(agent.maxBlocks)} />
              <ReadOnlyField label="Block Hold Hours" value={formatNumber(agent.blockHoldHours)} />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          {onEdit && (
            <button className="btn btn-primary" onClick={() => {
              onClose();
              onEdit(agent);
            }}>
              <IconSystem.edit size={16} />
              Edit
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default AgentViewModal;
